import { Observable, type ReadonlyObservable } from "@/lib/studio/observable";
import type { LiveEvent, TimedLiveEvent } from "./live-events";
import {
  MAX_EVENTS_PER_FRAME,
  MAX_FRAME_SPAN_MS,
  decodeFrame,
  encodeGestures,
  encodeKeepalive,
  encodeState,
} from "./live-frame-codec";
import type { LocalAudioLatency } from "./live-latency";
import {
  canEmit,
  parseSessionState,
  type LivePeerAnnouncement,
  type LiveSessionState,
} from "./session-state";
import type {
  LiveConnectionState,
  LiveCredentials,
  LiveFramePayload,
  LiveParticipant,
  LiveTransport,
} from "./live-transport";
import { isNoteTraceEnabled, traceNote } from "./note-trace";
import { elapsedSinceStamp, recordTransit, stampOf, transitP90 } from "./transit";
import { MediaTiming, type SpeechTiming } from "./media-timing";
import { AudioEngine } from "./audio/audio-engine";
import type { AudioOutput } from "./audio/audio-types";
import { VideoEngine } from "./video/video-engine";
import type { VideoControl, VideoPairing } from "./video/video-types";

/**
 * Les colonnes d'un geste dans le journal de notes.
 *
 * La pédale n'a pas de note : elle prend `-1`, ce qui la range à part sans lui
 * inventer une touche. Voir `lib/live/note-trace.ts`.
 */
function traceShape(event: LiveEvent): {
  kind: "noteOn" | "noteOff" | "sustain";
  midi: number;
  velocity?: number;
} {
  if (event.kind === "noteOn") {
    return { kind: "noteOn", midi: event.midi, velocity: event.velocity };
  }

  if (event.kind === "noteOff") {
    return { kind: "noteOff", midi: event.midi };
  }

  return { kind: "sustain", midi: -1 };
}

/**
 * Une séance de jeu partagée.
 *
 * # Sa seule responsabilité
 *
 * Faire circuler des gestes. Elle ne sait pas d'où ils viennent — un clavier,
 * une souris, un morceau rejoué —, ni ce qu'on en fait à l'arrivée — un piano,
 * un affichage, rien. C'est ce qui la rend réutilisable : le pont vers la lane
 * du piano (`bridge/lane-live-bridge.ts`) est un client parmi d'autres
 * possibles.
 *
 * Elle ne connaît pas davantage LiveKit : elle parle à un `LiveTransport`.
 *
 * # Regroupement
 *
 * Les gestes émis dans le même tour de boucle partent dans une seule trame.
 * Au-delà, on envoie tout de suite : attendre coalescerait mieux, mais chaque
 * milliseconde d'attente est une milliseconde de retard chez l'autre. Une note
 * fait quatre octets, il n'y a rien à économiser qui vaille ce prix.
 *
 * # Le filet sous le mode non fiable
 *
 * Tout part en `reliable: false` — le canal fiable est ordonné, donc une perte
 * y bloquerait tout ce qui suit le temps d'une retransmission, et une note en
 * retard d'un aller-retour est pire qu'une note manquante.
 *
 * La contrepartie est qu'un note-off perdu laisse une touche baissée
 * indéfiniment. D'où la **trame d'état**, réémise toutes les
 * `stateIntervalMs` : elle réaffirme ce que ce participant tient, le récepteur
 * s'y aligne, et toute perte se rattrape en un quart de seconde. On la préfère
 * à un renvoi des note-off, qui pourrait couper une note rejouée entre-temps —
 * réaffirmer un état est idempotent, rejouer un relâchement ne l'est pas.
 *
 * L'état est tenu à partir de ce que la session a **elle-même émis**, et c'est
 * exactement la bonne source : ce qu'on réaffirme, c'est ce qu'on a dit aux
 * autres, pas ce qu'un objet tiers croit savoir.
 *
 * # L'annonce
 *
 * À chaque tour, la session mesure sa jambe jusqu'au SFU, la lisse, et la
 * remet au rappel `announcePeerInfo` avec la latence audio locale et le trajet
 * mesuré sur les trames reçues (voir `transit.ts` : chaque trame porte son
 * instant d'émission sur l'horloge du service, et la session lit la même
 * horloge à l'arrivée). Elle ne sait pas ce qu'il en fait : c'est le contexte
 * React qui la poste au service de temps, lequel en déduit le délai de
 * restitution de chacun — rendu à celui qui annonce, et publié pour toute la
 * salle en jam. C'est aussi l'annonce qui prend le lead quand il est vacant,
 * et qui le garde : un leader qui cesse d'annoncer le perd. Elle ne connaît ni
 * HTTP ni le service, et cette frontière est ce qui la rend testable sans
 * navigateur.
 *
 * Une version précédente diffusait la jambe aux pairs dans une trame dédiée,
 * et chaque client calculait son propre retard sur ce qu'il avait reçu. Trois
 * clients tenaient trois versions de la même règle ; la trame est partie avec.
 *
 * # Le rythme des annonces
 *
 * Une dès la connexion, puis une toutes les `peerIntervalMs` — deux
 * secondes —, et une toutes les demi-secondes tant que l'état publié dit que
 * le jam tourne. Et une tout de suite quand le régime change : jam allumé ou
 * éteint, lead qui change de mains.
 *
 * Hors jam, la réponse à l'annonce est le seul chemin par lequel un délai
 * nouveau arrive : quand le lead change de mains, le trajet de chaque auditeur
 * change avec lui, et attendre le tour suivant laisserait jusqu'à deux
 * secondes les notes du nouveau leader tomber derrière sa tête de lecture. En
 * jam, c'est le service qui a besoin des mesures de chacun, fraîches, pour
 * tenir le délai commun que toute la salle pose.
 *
 * Le tour suivant se réarme à chaque annonce plutôt que sur un intervalle posé
 * une fois : c'est ce qui relit le régime, et ce qui fait qu'une annonce
 * avancée par un changement repousse la suivante au lieu de la doubler.
 *
 * # Deux régimes, et la garde des deux côtés
 *
 * En régime de lead, seules les notes du leader circulent ; en jam, celles de
 * tout le monde. Le service publie qui mène et si le jam tourne, la session
 * le lit dans `sessionState`, et c'est **ici** que la règle s'applique, une
 * seule fois, plutôt que recopiée chez chaque consommateur :
 *
 * - à l'émission, `send` ne publie rien quand ce participant n'a pas le droit
 *   d'émettre. Il lui faut sa propre identité pour ça (`identity`) — celle du
 *   billet, la même que le service lit dans le jeton ;
 * - à la réception, une trame venue d'un autre que le leader est ignorée hors
 *   jam. C'est cette garde-là qui tient face à un client resté sur une version
 *   antérieure, qui émet sans se demander s'il a le droit — et c'est pour ça
 *   qu'elle ne peut pas être seulement côté émission.
 *
 * La garde de réception vaut pour les trames d'état autant que pour les
 * gestes : une trame d'état réaffirme des touches enfoncées, et un client
 * ancien qui la produirait ferait sonner ce que ses gestes ne peuvent plus.
 *
 * Un geste local qui n'est pas publié **sonne quand même chez soi** : rien ici
 * ne touche au son, qui est produit en amont par la lane du piano. L'auditeur
 * s'entend jouer, il n'envoie simplement rien — et ne retient rien non plus
 * pour ses trames d'état, qui ne réaffirment que ce qu'on a dit aux autres.
 *
 * Perdre le droit d'émettre au milieu d'un accord ne laisse pas de touche
 * baissée chez les autres : ce n'est pas la session qui s'en charge, mais la
 * piste des partenaires chez chaque récepteur, qui relâche ce qu'un
 * participant démis tenait — comme elle le fait pour un partant (voir
 * `LiveControler`). Ici, on oublie seulement ce qu'on tenait : on ne le
 * réaffirmera plus, et le noteOff qu'on ne peut plus envoyer n'est dû à
 * personne.
 *
 * # Le battement
 *
 * Sur demande, la session émet un battement à cadence fixe — une trame vide,
 * six octets — pour tenir les radios de la salle éveillées. **Seulement tant
 * que le jam tourne** : c'est le seul régime où la gigue de chacun fait le
 * délai de toute la salle, et où un Wi-Fi assoupi se paie en rythme. En lead,
 * chaque auditeur absorbe son propre trajet et personne ne dépend de lui ; le
 * battement de l'interprète partait alors vers tout le public pour rien.
 * Il part que la session ait ou non le droit d'émettre : il ne porte aucune
 * musique. À la réception il est ignoré avant toute garde. La cadence vient de
 * l'appelant, qui sait qui peut en avoir besoin — personne chez un spectateur ;
 * voir `lib/live/radio-keepalive.ts` pour la mesure qui l'a fixée.
 *
 * # Ce qui ne passe pas par ici
 *
 * La pause, la position, le tempo, le lead, le jam, les délais de
 * restitution : des faits de séance — rares, et sur lesquels deux
 * participants peuvent être en désaccord. Ils appartiennent au service de
 * temps, qui les publie dans la métadonnée de salle ; la session les lit dans
 * `sessionState` et ne les émet jamais. La règle de décision : si deux
 * participants peuvent en être en désaccord, ça passe par le service ; sinon,
 * par le canal de données.
 *
 * La pause a circulé ici aussi, de pair à pair, en parallèle de l'état publié.
 * Sur une vraie transition, le service publiait, chaque client appliquait, et
 * chaque client rediffusait : chacun adoptait la position du dernier émetteur
 * au lieu de celle du service. Deux autorités pour une même chose ; la trame
 * est partie avec la version 11 du format, et rien ici ne sait plus la
 * produire.
 */

export interface LiveSessionOptions {
  transport: LiveTransport;
  /**
   * Notre identité dans la salle — celle du billet, celle que le service
   * publie quand on mène. C'est ce qui permet à la session de savoir si elle
   * mène, donc si elle a le droit d'émettre. Vide par défaut : sans identité,
   * on n'est le leader de personne, et seul le jam laisse émettre.
   */
  identity?: string;
  /**
   * Fenêtre de regroupement des gestes, en millisecondes. `0` regroupe ce qui
   * arrive dans le même tour de boucle et rien de plus.
   */
  coalesceMs?: number;
  /**
   * Cadence des annonces hors jam, en millisecondes : deux secondes par
   * défaut. En jam, elle descend à une demi-seconde (voir « Le rythme des
   * annonces »). Zéro les désactive toutes, changements de régime compris
   * (tests).
   */
  peerIntervalMs?: number;
  /** Cadence des trames d'état. Zéro désactive le filet (tests). */
  stateIntervalMs?: number;
  /**
   * Cadence du battement qui tient les radios éveillées, en millisecondes,
   * tant que le jam tourne. Zéro — le défaut — n'en émet jamais. Voir
   * `lib/live/radio-keepalive.ts`.
   */
  keepaliveMs?: number;
  /**
   * Latence audio locale à annoncer, en millisecondes, décomposée en ses deux
   * moitiés. Injectée parce que la session n'a pas à savoir qu'il existe un
   * moteur audio — elle transporte le chiffre, elle ne le calcule pas.
   */
  localLatency?: () => LocalAudioLatency;
  /**
   * Reçoit, à chaque tour d'annonce, ce que cette machine coûte : sa jambe
   * lissée et sa latence audio. C'est l'appelant qui sait à qui le dire — la
   * session ne connaît ni HTTP ni le service de temps.
   *
   * Appelé sans attendre de retour. Une annonce perdue n'a pas de suite ici :
   * la prochaine part au tour suivant, et un rappel qui lève ne coupe ni la
   * session ni les annonces d'après. Le délai que le service rend ne repasse
   * pas par la session : c'est l'appelant qui poste, donc lui qui lit la
   * réponse.
   */
  announcePeerInfo?: (info: LivePeerAnnouncement) => void;
  /**
   * L'horloge du service, telle que ce poste la connaît : `performance.now()`
   * plus l'écart mesuré, en millisecondes. `null` tant que l'écart ne l'est
   * pas. Avec `positionSeconds`, c'est l'instant d'un geste sur l'horloge de
   * position — l'horodatage MIDI — porté sur cette même horloge.
   *
   * C'est ce qui date les trames, et ce qui mesure leur trajet à l'arrivée :
   * une seule horloge des deux côtés, celle du service. Voir `transit.ts`.
   */
  serverNow?: (positionSeconds?: number) => number | null;
  /**
   * Couper un appareil appairé : la route du service de séance, que l'appelant
   * connaît par son billet. Voir `VideoEngine.remove`.
   */
  videoControl?: VideoControl;
  /**
   * Créer et retirer un code d'appairage, pour le contexte de la séance — un
   * récital aujourd'hui. Sans lui, la séance ne propose pas d'appairer. Voir
   * `VideoEngine.pairDevice`.
   */
  videoPairing?: VideoPairing;
  /**
   * Où faire entendre le son de la séance. Le contexte donne une sortie sur le
   * contexte audio du studio, pour que le son sorte avec les notes. Voir
   * `AudioEngine`.
   */
  audioOutput?: AudioOutput;
  /**
   * Comment se cale la parole de la séance — une voix, un visage filmé : avec
   * les notes, comme le jeu, ou au plus tôt. Les notes par défaut, ce que
   * veulent un récital et un jam ; un cours fera passer la parole au plus tôt.
   * Voir `media-timing.ts`.
   */
  speechTiming?: SpeechTiming;
}

type RemoteEventsListener = (events: readonly TimedLiveEvent[], from: LiveParticipant) => void;
type RemoteStateListener = (events: readonly LiveEvent[], from: LiveParticipant) => void;
type ParticipantLeftListener = (participant: LiveParticipant) => void;
type ServerDataListener = (topic: string, payload: Uint8Array) => void;

const DEFAULT_STATE_INTERVAL_MS = 250;

/** Cadence des annonces hors jam. */
const DEFAULT_PEER_INTERVAL_MS = 2000;

/**
 * Cadence des annonces tant que le jam tourne.
 *
 * Tout le monde y joue sur le délai commun, et le service le tient sur les
 * mesures de chacun : plus elles sont fraîches, plus tôt il suit une liaison
 * qui se dégrade, et chaque note qu'il laisse passer d'ici là sonne hors de la
 * grille chez tout le monde. Une demi-seconde, quatre fois la cadence
 * ordinaire, pour le seul régime où la salle entière dépend de chaque mesure.
 */
const JAM_PEER_INTERVAL_MS = 500;

/**
 * La fenêtre sur laquelle on lisse la jambe qu'on annonce, en millisecondes.
 *
 * On annonce le **plus court** des relevés de la fenêtre, jamais celui de
 * l'instant. Une liaison Wi-Fi qui sort de veille, l'économie d'énergie d'un
 * iPad, une file d'attente passagère : un relevé isolé peut valoir six fois
 * les autres — mesuré, pas supposé.
 *
 * Et ce chiffre ne sert pas qu'à l'affichage : le service en déduit le délai
 * de restitution de **toute la salle**, sur les deux plus grandes jambes
 * annoncées. Il monte tout de suite et ne redescend qu'après une longue
 * accalmie, par pas — un seul pic annoncé par un seul participant retarderait
 * donc le son de tout le monde pendant des dizaines de secondes, et chacun
 * entendrait son propre jeu partir en retard sans comprendre pourquoi.
 *
 * Le minimum plutôt que la médiane : un aller-retour court est un aller-retour
 * où personne n'a fait la queue. C'est la mesure la moins polluée du lot, et
 * une vraie dégradation la fait monter en quelques tours de toute façon.
 *
 * # En temps, pas en nombre de relevés
 *
 * La fenêtre comptait quatre relevés, un par annonce toutes les deux secondes.
 * En jam, les annonces partent toutes les demi-secondes, et quatre relevés n'y
 * couvraient plus que deux secondes — moins que l'intervalle auquel le
 * navigateur rafraîchit l'aller-retour (voir `transit.ts`). Un pic y restait
 * seul et partait tel quel, dans le régime précisément où il retarde toute la
 * salle. Sept secondes tiennent les quatre relevés d'autrefois, quelle que
 * soit la cadence.
 */
const LEG_WINDOW_MS = 7_000;

export class LiveSession {
  private readonly transport: LiveTransport;
  private readonly identity: string;
  /** Le moteur vidéo de la séance, sur la même salle que les notes. Voir `docs/spec/video-engine.md`. */
  readonly video: VideoEngine;
  /** Le moteur audio de la séance, sur la même salle. Voir `docs/spec/audio-engine.md`. */
  readonly audio: AudioEngine;
  /**
   * Le calage que les deux moteurs partagent : l'horloge et le délai des notes,
   * que le contexte y pose, et la parole, qu'une voix et un visage d'un même
   * compte tiennent ensemble quand elle passe au plus tôt. Voir
   * `media-timing.ts`.
   */
  readonly timing: MediaTiming;
  private readonly coalesceMs: number;
  private readonly stateIntervalMs: number;
  private readonly peerIntervalMs: number;
  private readonly keepaliveMs: number;
  private readonly localLatency: () => LocalAudioLatency;
  private readonly announcePeerInfo: (info: LivePeerAnnouncement) => void;
  private readonly serverNow: (positionSeconds?: number) => number | null;

  private readonly _state = new Observable<LiveConnectionState>("idle");
  private readonly _participants = new Observable<readonly LiveParticipant[]>([]);

  /**
   * L'état publié par le service de temps. `null` tant que rien n'a été lu.
   *
   * Il arrive à la jointure sans qu'on ait rien demandé, puis à chaque
   * changement. C'est ce qui remplace la poignée de main qu'un arrivant devait
   * faire pour savoir où en était la séance — et c'est là que se lisent le
   * régime, le délai commun du jam et l'audience.
   */
  private readonly _sessionState = new Observable<LiveSessionState | null>(null);

  /**
   * Les derniers relevés de notre jambe, chacun avec son instant
   * (`performance.now()`). Voir `LEG_WINDOW_MS`.
   */
  private readonly ownLegSamples: { legMs: number; at: number }[] = [];

  private readonly remoteListeners = new Set<RemoteEventsListener>();
  private readonly stateListeners = new Set<RemoteStateListener>();
  private readonly leftListeners = new Set<ParticipantLeftListener>();
  private readonly serverDataListeners = new Set<ServerDataListener>();

  /**
   * La salle nous a sortis, et ce n'est ni la fin ni la liaison. Posé par le
   * transport juste avant « fermé », lu par qui décide où l'on va ensuite.
   */
  private _evicted = false;

  private pending: TimedLiveEvent[] = [];
  private flushHandle: ReturnType<typeof setTimeout> | null = null;
  private flushScheduled = false;

  private stateHandle: ReturnType<typeof setInterval> | null = null;
  /** Le prochain tour d'annonce, réarmé à chaque tour. `null` quand le rythme ne tourne pas. */
  private peerHandle: ReturnType<typeof setTimeout> | null = null;
  private keepaliveHandle: ReturnType<typeof setInterval> | null = null;

  /** Vrai entre `startTimers` et `stopTimers` : les rythmes de fond ont le droit de tourner. */
  private timersRunning = false;

  /** Ce que cette session a annoncé tenir : la source de ses trames d'état. */
  private readonly localHeld = new Map<number, number>();
  private localSustain = false;

  private disposed = false;

  constructor(options: LiveSessionOptions) {
    this.transport = options.transport;
    this.identity = options.identity ?? "";
    this.coalesceMs = Math.max(0, options.coalesceMs ?? 0);
    this.stateIntervalMs = Math.max(0, options.stateIntervalMs ?? DEFAULT_STATE_INTERVAL_MS);
    this.peerIntervalMs = Math.max(0, options.peerIntervalMs ?? DEFAULT_PEER_INTERVAL_MS);
    this.keepaliveMs = Math.max(0, options.keepaliveMs ?? 0);
    this.localLatency = options.localLatency ?? (() => ({ lookAheadMs: 0, outputMs: 0 }));
    this.announcePeerInfo = options.announcePeerInfo ?? (() => undefined);
    this.serverNow = options.serverNow ?? (() => null);
    this.timing = new MediaTiming({ speech: options.speechTiming });
    this.video = new VideoEngine({
      transport: options.transport.video,
      control: options.videoControl,
      pairing: options.videoPairing,
      timing: this.timing,
    });
    this.audio = new AudioEngine({ transport: options.transport.audio, output: options.audioOutput, timing: this.timing });
  }

  get state(): ReadonlyObservable<LiveConnectionState> {
    return this._state;
  }

  /** Les autres. Le participant local n'y figure jamais. */
  get participants(): ReadonlyObservable<readonly LiveParticipant[]> {
    return this._participants;
  }

  /** Où en est la séance, selon le service de temps. */
  get sessionState(): ReadonlyObservable<LiveSessionState | null> {
    return this._sessionState;
  }

  /** Vrai quand la fermeture vient d'une exclusion. Voir `LiveTransportHandlers.onEvicted`. */
  get evicted(): boolean {
    return this._evicted;
  }

  get isConnected(): boolean {
    return this._state.value === "connected";
  }

  /**
   * Ce participant a-t-il le droit d'émettre, selon le dernier état publié.
   *
   * La règle vit dans `canEmit` ; ceci n'est que sa lecture sur notre état et
   * notre identité, exposée pour que l'appelant n'ait pas à la recomposer.
   *
   * Le rideau n'entre pas ici : il ne retire à personne le droit d'émettre, il
   * ferme la sortie. La nuance compte — un droit perdu fait relâcher ce qu'on
   * tenait chez les autres (voir `applySessionState`), un rideau non : les
   * récepteurs n'ont déjà plus rien de nous, puisqu'il les a fait relâcher en
   * tombant. Voir `broadcasting`.
   */
  get emitting(): boolean {
    return canEmit(this._sessionState.value, this.identity);
  }

  /**
   * Ce poste envoie-t-il vraiment ses gestes : le droit d'émettre, **et** le
   * rideau levé.
   *
   * Le rideau est un fait de séance (`curtain`), publié par le service : c'est
   * ce qui permet à l'émetteur de se taire et au récepteur d'écarter ce qui
   * serait encore en vol, sans qu'ils aient à se le dire. Voir la garde
   * symétrique dans `receive`.
   */
  private get broadcasting(): boolean {
    return this.emitting && this._sessionState.value?.curtain !== true;
  }

  async connect(credentials: LiveCredentials): Promise<void> {
    if (this.disposed) {
      throw new Error("LiveSession: session déjà libérée.");
    }

    this._state.set("connecting");
    this._evicted = false;

    try {
      await this.transport.connect(credentials, {
        onState: (state) => this._state.set(state),
        onEvicted: () => {
          this._evicted = true;
        },
        onData: (payload, from) => this.receive(payload, from),
        onParticipants: (participants) => this.updateParticipants(participants),
        onSessionState: (payload) => this.applySessionState(parseSessionState(payload)),
        onServerData: (topic, payload) => {
          for (const listener of this.serverDataListeners) {
            listener(topic, payload);
          }
        },
      });
    } catch (error) {
      this._state.set("failed");

      throw error;
    }

    this.startTimers();
  }

  async disconnect(): Promise<void> {
    this.stopTimers();
    this.cancelFlush();
    this.pending = [];
    this.localHeld.clear();
    this.localSustain = false;
    this.ownLegSamples.length = 0;
    this._participants.set([]);
    this.video.release();
    this.audio.release();
    await this.transport.disconnect();
    this._state.set("closed");
  }

  /**
   * Met un geste en route, à sa position sur le transport partagé. Sans effet
   * hors connexion : l'appelant n'a donc pas à surveiller l'état pour savoir
   * s'il peut jouer — c'est tout l'intérêt, le pont reste branché en
   * permanence.
   *
   * Le `tick` vient de l'appelant et non d'ici : la session ne connaît pas le
   * transport, et c'est ce qui lui permet de servir aussi bien un piano qu'un
   * enregistreur. `at`, l'instant du geste sur l'horloge de position quand la
   * source le connaît, date la trame au geste plutôt qu'à l'envoi.
   */
  send(event: LiveEvent, tick: number, at?: number): void {
    if (!this.isConnected) {
      return;
    }

    // Un auditeur n'émet rien : ni trame, ni trace « sent », ni mémoire pour
    // ses trames d'état. Le geste a déjà sonné chez lui, en amont — la lane du
    // piano ne passe pas par ici pour ça. Voir « la garde des deux côtés ».
    //
    // Rideau baissé, même silence, et pour la même raison de mémoire : ce qu'on
    // joue derrière le rideau n'est dû à personne, et une trame d'état qui le
    // réaffirmerait au relevé ferait sonner des touches relâchées entre-temps.
    if (!this.broadcasting) {
      return;
    }

    this.rememberLocal(event);
    this.pending.push({ event, at: this.stamp(at), tick });

    if (isNoteTraceEnabled()) {
      traceNote({ role: "sent", lane: "moi", ...traceShape(event), tick });
    }

    // Une trame ne peut pas porter plus que son plafond : au-delà on part tout
    // de suite plutôt que de tronquer.
    if (this.pending.length >= MAX_EVENTS_PER_FRAME) {
      this.flush();

      return;
    }

    this.scheduleFlush();
  }

  onRemoteEvents(listener: RemoteEventsListener): () => void {
    this.remoteListeners.add(listener);

    return () => {
      this.remoteListeners.delete(listener);
    };
  }

  /**
   * Notifie l'état complet d'un pair : ce qu'il tient à cet instant. L'abonné
   * doit s'y **aligner** — enfoncer ce qui manque, relâcher ce qui est en trop
   * — et non l'appliquer comme une suite de gestes.
   */
  onRemoteState(listener: RemoteStateListener): () => void {
    this.stateListeners.add(listener);

    return () => {
      this.stateListeners.delete(listener);
    };
  }

  onParticipantLeft(listener: ParticipantLeftListener): () => void {
    this.leftListeners.add(listener);

    return () => {
      this.leftListeners.delete(listener);
    };
  }

  /**
   * Ce que le service dit à la salle sur le canal de données — le fil, les
   * réactions —, sujet par sujet, tel quel. La session ne le lit pas : ce
   * n'est pas de la musique, et aucune garde de jeu ne s'y applique (voir
   * `lib/live/hall.ts`). Elle ne fait que le tendre à qui l'écoute.
   */
  onServerData(listener: ServerDataListener): () => void {
    this.serverDataListeners.add(listener);

    return () => {
      this.serverDataListeners.delete(listener);
    };
  }

  dispose(): void {
    this.disposed = true;
    this.stopTimers();
    this.cancelFlush();
    this.remoteListeners.clear();
    this.stateListeners.clear();
    this.leftListeners.clear();
    this.serverDataListeners.clear();
    this._state.dispose();
    this._participants.dispose();
    this.video.dispose();
    this.audio.dispose();
    this.timing.dispose();
    this._sessionState.dispose();
  }

  // ── Rythmes de fond ──────────────────────────────────────────

  private startTimers(): void {
    if (this.stateIntervalMs > 0 && this.stateHandle === null) {
      this.stateHandle = setInterval(() => this.publishState(), this.stateIntervalMs);
    }

    if (this.peerIntervalMs > 0 && this.peerHandle === null) {
      // Tout de suite, puis en cadence : tant que le service ne connaît pas
      // notre jambe, le délai qu'il calcule ne la couvre pas, et nos notes
      // peuvent tomber derrière la tête de lecture des autres. Et c'est la
      // réponse à cette annonce qui apporte notre propre délai.
      this.announceNow();
    }

    this.timersRunning = true;
    this.syncKeepalive();
  }

  /**
   * Allume ou coupe le battement selon le régime publié : il ne bat que tant
   * que le jam tourne. Voir « Le battement ».
   */
  private syncKeepalive(): void {
    const wanted = this.timersRunning && this.keepaliveMs > 0 && this._sessionState.value?.jam === true;

    if (wanted && this.keepaliveHandle === null) {
      this.keepaliveHandle = setInterval(() => this.beat(), this.keepaliveMs);

      return;
    }

    if (!wanted && this.keepaliveHandle !== null) {
      clearInterval(this.keepaliveHandle);
      this.keepaliveHandle = null;
    }
  }

  private stopTimers(): void {
    this.timersRunning = false;

    if (this.peerHandle !== null) {
      clearTimeout(this.peerHandle);
      this.peerHandle = null;
    }

    if (this.keepaliveHandle !== null) {
      clearInterval(this.keepaliveHandle);
      this.keepaliveHandle = null;
    }

    if (this.stateHandle !== null) {
      clearInterval(this.stateHandle);
      this.stateHandle = null;
    }
  }

  /**
   * Réaffirme ce que cette session tient. Émise même à vide : c'est ainsi
   * qu'un récepteur dont on a perdu le dernier note-off apprend qu'il ne
   * devrait plus rien tenir. La trame porte toujours l'état de pédale, donc
   * n'est jamais vide au sens du format.
   */
  private publishState(): void {
    // Même garde qu'à l'émission des gestes : un auditeur ne réaffirme rien.
    // Sa mémoire est vide de toute façon — rien n'y entre sans le droit
    // d'émettre — mais une trame d'état vide porte encore la pédale.
    if (!this.isConnected || !this.broadcasting) {
      return;
    }

    const events: LiveEvent[] = [{ kind: "sustain", down: this.localSustain }];

    for (const [midi, velocity] of this.localHeld) {
      if (events.length >= MAX_EVENTS_PER_FRAME) {
        break;
      }

      events.push({ kind: "noteOn", midi, velocity });
    }

    this.publish(encodeState(this.stamp(), events));
  }

  /** Un battement : six octets, pour que la radio ne s'endorme pas. */
  private beat(): void {
    if (!this.isConnected) {
      return;
    }

    this.publish(encodeKeepalive(this.stamp()));
  }

  /**
   * Annonce tout de suite, et réarme le tour suivant à la cadence du régime en
   * vigueur.
   *
   * Réarmé plutôt que répété : la cadence se relit à chaque tour, et une
   * annonce avancée par un changement de régime repousse la suivante — deux
   * annonces à quelques millisecondes d'écart n'apprendraient rien de plus au
   * service.
   */
  private announceNow(): void {
    if (this.peerHandle !== null) {
      clearTimeout(this.peerHandle);
    }

    void this.announce();

    this.peerHandle = setTimeout(() => {
      this.peerHandle = null;
      this.announceNow();
    }, this.announcementCadenceMs());
  }

  /**
   * L'attente jusqu'au prochain tour : une demi-seconde tant que l'état publié
   * dit que le jam tourne — jamais plus lente que hors jam —, `peerIntervalMs`
   * sinon.
   */
  private announcementCadenceMs(): number {
    if (this._sessionState.value?.jam === true) {
      return Math.min(this.peerIntervalMs, JAM_PEER_INTERVAL_MS);
    }

    return this.peerIntervalMs;
  }

  /**
   * Un tour d'annonce : mesurer la jambe, la lisser, la remettre au rappel.
   *
   * La jambe est remesurée à chaque tour plutôt que retenue : elle bouge avec
   * le réseau, et sa fraîcheur est tout ce qui fait la valeur du chiffre chez
   * le service. Rien ne part tant qu'elle n'a jamais été mesurée — annoncer
   * zéro dirait que la liaison est instantanée, pas qu'on ne sait pas encore.
   */
  private async announce(): Promise<void> {
    if (!this.isConnected) {
      return;
    }

    const leg = await this.transport.localRoundTripMs();

    if (!this.isConnected) {
      return;
    }

    const smoothed = this.smoothedLeg(leg);

    if (smoothed === null) {
      return;
    }

    const local = this.localLatency();

    try {
      this.announcePeerInfo({
        legMs: smoothed,
        lookAheadMs: local.lookAheadMs,
        outputMs: local.outputMs,
        // Zéro quand rien n'a encore été reçu : le service retombe alors sur
        // son estimation à partir des jambes, qui vaut mieux que rien le temps
        // que les premières notes traversent.
        transitMs: transitP90() ?? 0,
      });
    } catch (cause) {
      // Le rappel appartient à l'appelant. S'il lève, c'est son défaut, pas
      // une raison de couper la session ni les annonces suivantes : le service
      // oublierait notre jambe et calculerait le délai de la salle sans nous.
      console.warn("[live] annonce en échec", cause);
    }
  }

  /**
   * La jambe qu'on annonce : la plus courte de la fenêtre, ou `null` tant que
   * rien n'a été mesuré.
   *
   * Un relevé absent — connexion pas encore établie, statistiques pas encore
   * disponibles — ne rentre pas dans la fenêtre et ne la vide pas : il ne dit
   * pas que la liaison est bonne, il dit qu'on ne sait pas encore. On répète
   * alors ce qu'on sait, pour que le service garde notre jambe vivante — d'où
   * le dernier relevé, gardé même sorti de la fenêtre.
   */
  private smoothedLeg(leg: number | null): number | null {
    const now = performance.now();

    if (leg !== null) {
      this.ownLegSamples.push({ legMs: leg, at: now });
    }

    while (this.ownLegSamples.length > 1 && now - this.ownLegSamples[0].at > LEG_WINDOW_MS) {
      this.ownLegSamples.shift();
    }

    if (this.ownLegSamples.length === 0) {
      return null;
    }

    return Math.min(...this.ownLegSamples.map((sample) => sample.legMs));
  }

  // ── Émission ─────────────────────────────────────────────────

  private rememberLocal(event: LiveEvent): void {
    if (event.kind === "noteOn") {
      this.localHeld.set(event.midi, event.velocity);

      return;
    }

    if (event.kind === "noteOff") {
      this.localHeld.delete(event.midi);

      return;
    }

    if (event.kind === "sustain") {
      this.localSustain = event.down;

      return;
    }
  }

  /**
   * L'horodatage d'une trame : l'horloge du service, réduite à 32 bits, ou
   * zéro tant qu'on ne la connaît pas. Voir `stampOf`.
   */
  private stamp(positionSeconds?: number): number {
    return stampOf(this.serverNow(positionSeconds));
  }

  private publish(payload: LiveFramePayload): void {
    this.transport.publish(payload, { reliable: false });
  }

  private scheduleFlush(): void {
    if (this.flushScheduled) {
      return;
    }

    this.flushScheduled = true;

    if (this.coalesceMs === 0) {
      queueMicrotask(() => this.flush());

      return;
    }

    this.flushHandle = setTimeout(() => this.flush(), this.coalesceMs);
  }

  private cancelFlush(): void {
    if (this.flushHandle !== null) {
      clearTimeout(this.flushHandle);
      this.flushHandle = null;
    }

    this.flushScheduled = false;
  }

  private flush(): void {
    this.cancelFlush();

    if (this.pending.length === 0 || !this.isConnected) {
      this.pending = [];

      return;
    }

    const batch = this.pending;
    this.pending = [];

    for (const frame of splitIntoFrames(batch)) {
      this.publish(encodeGestures(frame.sentAt, frame.events));
    }
  }

  // ── Réception ────────────────────────────────────────────────

  /**
   * Le nouvel état publié, et ce qu'on en tire avant de le rendre.
   *
   * Si le droit d'émettre nous est retiré — lead perdu, jam éteint alors
   * qu'on ne mène pas —, ce qu'on tenait n'est plus dû à personne : les
   * récepteurs relâchent d'eux-mêmes ce qu'un participant démis tenait, et
   * une trame d'état qui le réaffirmerait plus tard, lead retrouvé, ferait
   * sonner des touches qu'on a relâchées entre-temps sans pouvoir le dire.
   *
   * L'oubli précède la publication de l'état : un abonné qui lirait
   * `sessionState` dans son rappel doit trouver la session déjà cohérente
   * avec ce qu'il lit.
   *
   * Et si le régime a changé, on annonce sans attendre le tour : le délai de
   * chacun a changé avec lui, et c'est la réponse à l'annonce qui l'apporte.
   * L'annonce suit la publication, pour que le tour suivant se réarme à la
   * cadence du régime qu'on vient de lire. Rien tant que le rythme ne tourne
   * pas : la connexion annonce d'elle-même en le lançant.
   */
  private applySessionState(state: LiveSessionState | null): void {
    const previous = this._sessionState.value;

    // Le rideau qui tombe vaut ici un droit perdu : ce qu'on tenait devant la
    // salle, elle l'a relâché — la garde de réception écarte tout ce qui
    // arrive maintenant, y compris les note-off. L'oublier est ce qui évite
    // qu'une trame d'état, au relevé, réaffirme des touches lâchées derrière.
    if (!canEmit(state, this.identity) || state?.curtain === true) {
      this.localHeld.clear();
      this.localSustain = false;
    }

    // La caméra suit le même rideau, et par le même chemin : c'est l'état
    // publié qui décide, pas le bouton — un onglet rouvert derrière un rideau
    // déjà baissé le trouve baissé, et un téléphone appairé le lit de son côté
    // (`LiveKitPairedDevice`). Le microphone, lui, n'est publié par personne
    // aujourd'hui ; le jour où il le sera, il passera par ici aussi.
    if ((previous?.curtain === true) !== (state?.curtain === true)) {
      void this.video.setCurtain(state?.curtain === true);
    }

    // Le cadrage des caméras suit le même chemin, et pour la même raison : il
    // est décidé une fois et publié pour tout le monde. Le moteur n'en retient
    // que le sujet — la découpe et le quart de tour se font sur l'appareil qui
    // filme, avant son encodeur, et n'arrivent jamais jusqu'ici.
    this.video.setFramings(state?.cameras ?? {});

    this._sessionState.set(state);
    this.syncKeepalive();

    if (this.peerHandle !== null && regimeChanged(previous, state)) {
      this.announceNow();
    }
  }

  private receive(payload: Uint8Array, from: LiveParticipant): void {
    const frame = decodeFrame(payload);

    // Une trame illisible vient d'une version qu'on ne connaît pas, ou d'un
    // datagramme abîmé. On l'ignore : c'est la seule réaction qui n'aggrave pas.
    if (frame === null) {
      return;
    }

    // Un battement ne porte rien et vient de n'importe qui : rien à appliquer,
    // et aucune garde à lui faire passer.
    if (frame.type === "keepalive") {
      return;
    }

    // Une trame venue de quelqu'un qui n'a pas le droit d'émettre est ignorée
    // — gestes et état confondus. C'est la garde qui tient face à un client
    // ancien, et face à un leader démis dont une trame était encore en vol.
    //
    // Rideau baissé, plus rien n'entre, de personne : même garde, et c'est elle
    // qui rend le rideau vrai chez le spectateur, quel que soit l'âge du client
    // d'en face. Voir `broadcasting` pour la moitié émettrice.
    if (!canEmit(this._sessionState.value, from.id) || this._sessionState.value?.curtain === true) {
      return;
    }

    if (frame.type === "gestures") {
      this.measureTransit(frame.events);

      if (isNoteTraceEnabled()) {
        for (const timed of frame.events) {
          traceNote({
            role: "recv",
            lane: from.id,
            ...traceShape(timed.event),
            tick: timed.tick,
            sentAt: timed.at,
          });
        }
      }

      for (const listener of this.remoteListeners) {
        listener(frame.events, from);
      }

      return;
    }

    // Il ne reste que l'état : ce qu'un pair tient, à s'aligner dessus.
    for (const listener of this.stateListeners) {
      listener(frame.events, from);
    }
  }

  /**
   * Le trajet de chaque geste reçu : son horodatage contre notre lecture de la
   * même horloge. Rien tant que l'un des deux ne la connaît pas.
   */
  private measureTransit(events: readonly TimedLiveEvent[]): void {
    const now = this.serverNow();

    if (now === null) {
      return;
    }

    for (const timed of events) {
      const transit = elapsedSinceStamp(timed.at, now);

      if (transit !== null) {
        recordTransit(transit);
      }
    }
  }

  /**
   * La liste des autres, telle que le transport la donne.
   *
   * Une arrivée n'appelle pas d'annonce : ce qu'on a annoncé, le service le
   * tient, et le nouveau venu n'en a pas l'usage. Il n'y a rien à lui répéter.
   */
  private updateParticipants(next: readonly LiveParticipant[]): void {
    const previous = this._participants.value;
    const stillHere = new Set(next.map((participant) => participant.id));

    this._participants.set(next);

    for (const gone of previous.filter((participant) => !stillHere.has(participant.id))) {
      for (const listener of this.leftListeners) {
        listener(gone);
      }
    }
  }
}

/**
 * Le régime a-t-il changé d'un état publié à l'autre : jam allumé ou éteint,
 * lead qui change de mains.
 *
 * Un état absent vaut une salle sans jam ni lead, ce qu'est une séance tant
 * que rien n'est publié : le premier état qui dit l'un ou l'autre change bien
 * le régime pour qui annonçait déjà. Le reste — position, pause, délai commun,
 * audience — n'appelle pas d'annonce : le délai commun se lit dans l'état
 * même, et le reste ne touche au délai de personne.
 */
function regimeChanged(previous: LiveSessionState | null, next: LiveSessionState | null): boolean {
  if ((previous?.jam ?? false) !== (next?.jam ?? false)) {
    return true;
  }

  return (previous?.lead ?? "") !== (next?.lead ?? "");
}

/**
 * Découpe une salve en trames encodables.
 *
 * Deux bornes s'imposent : le nombre d'événements qu'une trame peut porter, et
 * l'écart maximal qu'un octet d'écart sait exprimer.
 */
export function splitIntoFrames(events: readonly TimedLiveEvent[]) {
  const frames: { sentAt: number; events: TimedLiveEvent[] }[] = [];

  for (const timed of events) {
    const current = frames[frames.length - 1];
    const fits =
      current !== undefined &&
      current.events.length < MAX_EVENTS_PER_FRAME &&
      timed.at - current.sentAt <= MAX_FRAME_SPAN_MS;

    if (fits) {
      current.events.push(timed);

      continue;
    }

    frames.push({ sentAt: timed.at, events: [timed] });
  }

  return frames;
}
